import React, { useState } from 'react';
import { TradingActivity, TradingActivitiesData } from '../../features/portfolio/types/tradingActivity';
import { normalizeToUtc20, getWeekStartMonday } from '../utils/dateHelpers';

// Props interface for the marker component
interface TradingActivityMarkerProps {
  cx?: number;
  cy?: number;
  payload?: any;
  originalDateStr: string;
  activities: TradingActivitiesData;
  onMarkerClick: (date: string, activities: TradingActivity[]) => void;
  groupingMs?: number;
  ticker?: string; // Add optional ticker filter
  view?: 'pct' | 'dollar' | 'dol1' | 'dol2'; // Current view mode for filtering
  chartDataPoints?: number[]; // Array of timestamps for all chart data points (market days)
}

// Component for rendering trading activity markers on charts
const TradingActivityMarker: React.FC<TradingActivityMarkerProps> = ({
  cx = 0,
  cy = 0,
  payload,
  originalDateStr,
  activities,
  onMarkerClick,
  groupingMs = 0,
  ticker,
  view,
  chartDataPoints = []
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Convert timestamp to YYYY-MM-DD format (UTC string can shift day vs local)
  // We'll try exact match first, then fall back to nearest-date matching to be robust to TZ shifts
  const dateStr = new Date(payload.time).toISOString().split('T')[0];
  
  // Build a Set of all chart data point dates (market days) for quick lookup
  const marketDayDates = new Set<string>();
  if (chartDataPoints.length > 0) {
    chartDataPoints.forEach(timestamp => {
      const date = new Date(timestamp).toISOString().split('T')[0];
      marketDayDates.add(date);
    });
  }
  
  // 🔑 Smart matching: Use exact vs fuzzy matching based on grouping setting
  let dayKey: string | undefined;
  
  if (!groupingMs || groupingMs === 0) {
    // Exact date matching for daily views (6m and below) or when no grouping specified
    dayKey = Object.keys(activities).find(k => k.startsWith(dateStr));
    // Fallback: try nearest date within 36 hours BACKWARD to account for timezone offsets
    if (!dayKey) {
      const toleranceMs = 36 * 60 * 60 * 1000; // 36h
      let bestKey: string | undefined;
      let bestDiff = Number.POSITIVE_INFINITY;
      for (const k of Object.keys(activities)) {
        const activityTime = new Date(k).getTime();
        const diff = payload.time - activityTime; // Only look backward (positive diff)
        if (diff >= 0 && diff <= toleranceMs && diff < bestDiff) {
          bestDiff = diff;
          bestKey = k;
        }
      }
      dayKey = bestKey;
    }
  } else {
    // Weekly views (1y/2y) - Monday-Sunday buckets
    // Get the Monday 00:00 UTC of the week for the chart point
    const chartPointMonday = getWeekStartMonday(payload.time);
    const chartPointMondayStr = new Date(chartPointMonday).toISOString().split('T')[0];
    
    // Try exact match first (Monday 00:00 UTC date string)
    dayKey = Object.keys(activities).find(k => k.startsWith(chartPointMondayStr));
    
    // If exact match failed, try fuzzy matching within the week (Monday 00:00 UTC to Sunday 23:59)
    if (!dayKey) {
      const weekStart = chartPointMonday; // Monday 00:00 UTC
      const weekEnd = weekStart + (7 * 24 * 60 * 60 * 1000) - 1; // Sunday 23:59:59.999
      
      dayKey = Object.keys(activities).find(k => {
        const activityTime = new Date(k).getTime();
        const inRange = activityTime >= weekStart && activityTime <= weekEnd;
        return inRange;
      });
    }
  }
  
  const dayData = dayKey ? activities[dayKey] : undefined;
  
  // Collect activities that should be displayed on this chart point
  // This includes:
  // 1. Activities that match this chart point's date (normal case)
  // 2. Activities on weekend/non-market days that should be displayed on this chart point
  //    (this chart point is the last market day before the activity date, within 5 days)
  let allActivitiesForThisPoint: TradingActivity[] = [];
  
  // First, add activities that directly match this chart point
  if (dayData && dayData.activities.length > 0) {
    allActivitiesForThisPoint.push(...dayData.activities);
  }
  
  // Second, find activities on non-market days that should be displayed on this chart point
  // Only do this for daily views (not weekly views)
  if ((!groupingMs || groupingMs === 0) && chartDataPoints.length > 0) {
    const currentChartPointTime = payload.time;
    const maxLookbackMs = 5 * 24 * 60 * 60 * 1000; // 5 days
    
    // Iterate through all activity dates
    for (const activityDateStr of Object.keys(activities)) {
      // Skip if this activity date is already a market day (it has its own chart point)
      if (marketDayDates.has(activityDateStr)) {
        continue;
      }
      
      const activityDayData = activities[activityDateStr];
      if (!activityDayData || activityDayData.activities.length === 0) {
        continue;
      }
      
      // Use the first activity's timestamp to determine the activity time
      // All activities on the same date should have similar timestamps
      const firstActivity = activityDayData.activities[0];
      const activityTime = Date.parse(firstActivity.timestamp);
      const timeDiff = activityTime - currentChartPointTime;
      
      // Activity should be displayed on this chart point if:
      // - Activity date is after this chart point (within 5 days)
      // - This chart point is the last market day before the activity date
      if (timeDiff > 0 && timeDiff <= maxLookbackMs) {
        // Check if this chart point is the last market day before the activity date
        // by verifying there are no other market days between this point and the activity
        let isLastMarketDay = true;
        for (const otherChartTime of chartDataPoints) {
          if (otherChartTime > currentChartPointTime && otherChartTime < activityTime) {
            // There's another market day between this point and the activity
            isLastMarketDay = false;
            break;
          }
        }
        
        if (isLastMarketDay) {
          // This activity should be displayed on this chart point
          allActivitiesForThisPoint.push(...activityDayData.activities);
        }
      }
    }
  }
  
  // Early return if no activities for this date
  if (allActivitiesForThisPoint.length === 0) {
    return <g />; // Return empty group for TypeScript compatibility
  }

  // Filter activities by ticker if ticker prop is provided
  let filteredActivities = allActivitiesForThisPoint;
  if (ticker) {
    filteredActivities = allActivitiesForThisPoint.filter(activity => activity.ticker === ticker);
  }

  // Filter CASH instrument events based on view mode
  // CASH events (DEPOSIT, WITHDRAWAL, ADJUSTMENT) should only show in dollar view for account/portfolio charts
  // CASH events should not show in ticker charts (dol1/dol2) as they're not relevant to individual tickers
  // INTEREST events are always shown regardless of view mode
  if (view === 'pct' || view === 'dol1' || view === 'dol2') {
    filteredActivities = filteredActivities.filter(activity => activity.instrument !== 'CASH');
  }
  // For view === 'dollar', keep CASH events (no filtering needed)

  // Early return if no activities match the filters
  if (filteredActivities.length === 0) {
    return <g />;
  }

  // Determine which instruments exist for this bucket
  // Equity entry/exit use event_type directly to avoid confusion with derived action
  const hasEquityEntry = filteredActivities.some(a => a.instrument === 'EQUITY' && (a.event_type === 'BUY' || a.event_type === 'SHORT_SELL'));
  const hasEquityExit = filteredActivities.some(a => a.instrument === 'EQUITY' && (a.event_type === 'SELL' || a.event_type === 'BUY_TO_COVER'));
  const hasOptionCall = filteredActivities.some(a => a.instrument === 'OPTION' && a.optionType === 'CALL');
  const hasOptionPut = filteredActivities.some(a => a.instrument === 'OPTION' && a.optionType === 'PUT');
  const hasDividend = filteredActivities.some(a => a.instrument === 'DIVIDEND');
  const hasCash = filteredActivities.some(a => a.instrument === 'CASH');
  const hasInterest = filteredActivities.some(a => a.instrument === 'INTEREST');

  // If nothing to render, exit early
  if (!hasEquityEntry && !hasEquityExit && !hasOptionCall && !hasOptionPut && !hasDividend && !hasCash && !hasInterest) {
    return <g />;
  }


  // For weekly views, only render the marker for the exact week match, not fuzzy matches
  if (groupingMs && groupingMs > 0) {
    // Check if this is an exact week match (Monday 00:00 UTC)
    const chartPointMonday = getWeekStartMonday(payload.time);
    const chartPointMondayStr = new Date(chartPointMonday).toISOString().split('T')[0];
    const isExactWeekMatch = dayKey === chartPointMondayStr;
    
    
    if (!isExactWeekMatch) {
      return <g />;
    }
  }

  // Handle marker click
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    
    // For weekly views, use the Monday bucket date (dayKey) instead of the activity date
    // For daily views, use the actual activity date
    let useDate: string;
    if (groupingMs && groupingMs > 0) {
      // Weekly view: use the Monday bucket date (dayKey is already the Monday date string)
      useDate = dayKey || originalDateStr;
    } else {
      // Daily view: use the actual activity date
      useDate = filteredActivities.length > 0 ? filteredActivities[0].date : originalDateStr;
    }
    
    // Normalize to 20:00 UTC for consistent timezone display
    const normalizedDate = normalizeToUtc20(useDate);
    onMarkerClick(normalizedDate, filteredActivities);
  };

  // Icon layout and vertical spacing
  const iconSize = 22;
  const iconOffset = iconSize / 2;
  const verticalSpacing = 16; // base spacing between stacked rows
  
  // Calculate maximum vertical extent for stacked icons
  // Icons can stack up to 5 rows: equity, options, dividend, cash, interest
  const maxStackHeight = iconSize + (verticalSpacing * 4); // 22 + 64 = 86px
  const hitAreaPaddingVertical = 4; // Vertical padding around icons (for easier clicking)
  // Narrow hit area to prevent overlap when icons are close together horizontally
  const hitAreaWidth = 18; // Narrower than icon (22px) to reduce side overlap
  const hitAreaHeight = maxStackHeight + (hitAreaPaddingVertical * 2); // 86 + 8 = 94px
  // Center the narrow hitbox on cx for accurate selection
  const hitAreaX = cx - hitAreaWidth / 2;
  const hitAreaY = cy - iconOffset - hitAreaPaddingVertical;
  
  const hoverStyle = {
    filter: isHovered ? 'brightness(1.1) drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
    transition: 'filter 0.2s ease',
    pointerEvents: 'none' // IMPORTANT: let the rect capture events, not the icons
  } as React.CSSProperties;

  return (
    <g style={{ pointerEvents: 'all' }}>
      {/* Large invisible hit area that covers all possible stacked icon positions */}
      <rect
        key="hitarea"
        x={hitAreaX}
        y={hitAreaY}
        width={hitAreaWidth}
        height={hitAreaHeight}
        fill="transparent"
        style={{ cursor: 'pointer', pointerEvents: 'all' }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
      />
      
      {/* Equity layer (on the line if present) */}
      {(hasEquityEntry || hasEquityExit) && (
        <image
          key="equity"
          href={hasEquityEntry && hasEquityExit ? '/icon-both-lrg.png' : (hasEquityEntry ? '/icon-pos-lrg.png' : '/icon-neg-lrg.png')}
          x={cx - iconOffset}
          y={cy - iconOffset}
          width={iconSize}
          height={iconSize}
          style={hoverStyle}
        />
      )}

      {/* Options layer (on the line if no equity; below equity otherwise) */}
      {(hasOptionCall || hasOptionPut) && (
        <image
          key="options"
          href={
            hasOptionCall && hasOptionPut
              ? '/icon-both-cp-lrg.png'
              : hasOptionCall
              ? '/icon-call-lrg.png'
              : '/icon-put-lrg.png'
          }
          x={cx - iconOffset}
          y={(cy - iconOffset) + ((hasEquityEntry || hasEquityExit) ? verticalSpacing : 0)}
          width={iconSize}
          height={iconSize}
          style={hoverStyle}
        />
      )}

      {/* Dividend layer
          - If equity exists: place below options (or one row down if no options)
          - If no equity and options exist: place one row below options
          - If only dividend exists: place on the line
      */}
      {hasDividend && (
        <image
          key="dividend"
          href={'/icon-div-lrg.png'}
          x={cx - iconOffset}
          y={(cy - iconOffset)
              + ((hasEquityEntry || hasEquityExit)
                  ? ((hasOptionCall || hasOptionPut) ? verticalSpacing * 2 : verticalSpacing)
                  : ((hasOptionCall || hasOptionPut) ? verticalSpacing : 0))}
          width={iconSize}
          height={iconSize}
          style={hoverStyle}
        />
      )}

      {/* Interest layer
          - Place below dividend, options, and equity layers
      */}
      {hasInterest && (
        <image
          key="interest"
          href={'/icon-int-lrg.png'}
          x={cx - iconOffset}
          y={(cy - iconOffset)
              + ((hasEquityEntry || hasEquityExit)
                  ? ((hasOptionCall || hasOptionPut)
                      ? (hasDividend ? verticalSpacing * 3 : verticalSpacing * 2)
                      : (hasDividend ? verticalSpacing * 2 : verticalSpacing))
                  : ((hasOptionCall || hasOptionPut)
                      ? (hasDividend ? verticalSpacing * 2 : verticalSpacing)
                      : (hasDividend ? verticalSpacing : 0)))}
          width={iconSize}
          height={iconSize}
          style={hoverStyle}
        />
      )}

      {/* Cash layer
          - Place below interest layer (if present) or below dividend/options/equity
      */}
      {hasCash && (
        <image
          key="cash"
          href={'/icon-cash-lrg.png'}
          x={cx - iconOffset}
          y={(cy - iconOffset)
              + ((hasEquityEntry || hasEquityExit)
                  ? ((hasOptionCall || hasOptionPut)
                      ? (hasDividend
                          ? (hasInterest ? verticalSpacing * 4 : verticalSpacing * 3)
                          : (hasInterest ? verticalSpacing * 3 : verticalSpacing * 2))
                      : (hasDividend
                          ? (hasInterest ? verticalSpacing * 3 : verticalSpacing * 2)
                          : (hasInterest ? verticalSpacing * 2 : verticalSpacing)))
                  : ((hasOptionCall || hasOptionPut)
                      ? (hasDividend
                          ? (hasInterest ? verticalSpacing * 3 : verticalSpacing * 2)
                          : (hasInterest ? verticalSpacing * 2 : verticalSpacing))
                      : (hasDividend
                          ? (hasInterest ? verticalSpacing * 2 : verticalSpacing)
                          : (hasInterest ? verticalSpacing : 0))))}
          width={iconSize}
          height={iconSize}
          style={hoverStyle}
        />
      )}
    </g>
  );
};

export default TradingActivityMarker; 